import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { LogOut, Home, Plus, List, Menu, X, User, Settings, DollarSign, Grid3x3, Tag, Package, Calendar, Users, BarChart3, MessageSquare } from 'lucide-react';
import { authApi, userProfileApi } from '../services/api';
import { UserProfile } from '../types';
import { isAdmin, isFairManager } from '../utils/rbac';
import { extractApiError } from '../utils/errorUtils';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

export default function FairManagerLayout() {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState<string>('');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const { isEnabled: rentalEquipmentEnabled } = useFeatureFlag('rental_equipment');
  const { isEnabled: pricingTemplateEnabled } = useFeatureFlag('fair_manager_pricing_template');

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const data = await authApi.getCurrentUser();
        setUserEmail(data.email || '');
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to fetch user info:', apiError);
      }
    };

    const fetchProfile = async () => {
      try {
        const profileData = await userProfileApi.getProfile();
        setProfile(profileData);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to fetch profile:', apiError);
      }
    };

    fetchUserInfo();
    fetchProfile();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  const handleLogout = () => {
    authApi.logout();
    setShowProfileMenu(false);
    navigate('/login');
  };

  const getUserInitials = () => {
    if (profile?.display_name) {
      return profile.display_name.charAt(0).toUpperCase();
    }
    if (userEmail) {
      return userEmail.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <div className="min-h-screen bg-teal-50">
      {/* Header matches standard design (Issue #1509) */}
      <nav className="bg-primary text-white shadow-lg">
        <div className="w-full pl-0 pr-3 py-0.5">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-1.5">
              {/* Hamburger menu button (mobile only) */}
              <button
                type="button"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-md hover:bg-primary-dark"
                aria-label="Toggle menu"
                title="Toggle menu"
              >
                {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>

              <Link to="/fairmanager/dashboard" className="flex items-center space-x-1.5">
                {/* Logo matches main Navbar - 20% larger with 25% less padding (Issue #1509) */}
                <picture>
                  <source srcSet="/logo.webp" type="image/webp" />
                  <img src="/logo.png" alt="Local Artist Fairs" className="h-24 w-auto" />
                </picture>
                <span className="text-xs sm:text-sm bg-moonbone text-ink px-2 py-1 rounded">
                  Fair Administrator
                </span>
              </Link>
            </div>
            <div className="flex items-center space-x-1.5">
              <Link
                to="/"
                className="flex items-center space-x-1 hover:text-secondary-light transition-colors text-sm md:text-base"
              >
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">View Site</span>
              </Link>

              {/* Profile Avatar Dropdown */}
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center space-x-2 hover:text-secondary-light transition-colors focus:outline-none"
                  aria-label="User menu"
                  title="User menu"
                >
                  <div className="w-8 h-8 bg-teal-200 rounded-full flex items-center justify-center flex-shrink-0">
                    {profile?.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt="Profile"
                        className="w-8 h-8 rounded-full object-cover border-2 border-white"
                      />
                    ) : (
                      <span className="text-primary-dark font-semibold text-sm">{getUserInitials()}</span>
                    )}
                  </div>
                </button>

                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-sm text-gray-500">Signed in as</p>
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {profile?.display_name || userEmail?.split('@')[0] || 'User'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{userEmail}</p>
                      {isAdmin() && (
                        <p className="text-xs text-secondary font-medium mt-1">Administrator</p>
                      )}
                      {isFairManager() && (
                        <p className="text-xs text-secondary font-medium mt-1">Fair Administrator</p>
                      )}
                    </div>

                    <Link
                      to={isAdmin() ? "/admin/profile" : "/user/profile"}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <User className="h-4 w-4 mr-2" />
                      Manage My Profile
                    </Link>
                    <Link
                      to={isAdmin() ? "/admin/change-password" : "/user/change-password"}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Change Password
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-200"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="w-full px-1 py-4 lg:py-8">
        <div className="flex space-x-8">
          {/* Sidebar Navigation - Desktop */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow p-4">
              <nav className="space-y-2" data-testid="fairmanager-nav">
                <Link
                  to="/fairmanager/dashboard"
                  className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                >
                  <Home className="h-4 w-4" />
                  <span>Dashboard</span>
                </Link>
                <Link
                  to="/fairmanager/fairs"
                  className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                  data-testid="nav-my-fairs"
                >
                  <List className="h-4 w-4" />
                  <span>My Fairs</span>
                </Link>
                <Link
                  to="/fairmanager/fair/new"
                  className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                >
                  <Plus className="h-4 w-4" />
                  <span>Create New Fair</span>
                </Link>
                <Link
                  to="/fairmanager/payouts"
                  className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                  data-testid="nav-payouts"
                >
                  <DollarSign className="h-4 w-4" />
                  <span>Payouts</span>
                </Link>

                {/* Divider */}
                <div className="border-t border-gray-200 my-2"></div>

                {/* Note: Routes for the following items need to be added in follow-up work
                    (Issues #371, #372). These navigation links are intentionally included
                    to show the complete Fair Manager feature set, but will render blank
                    pages until the corresponding routes and components are implemented. */}
                <div className="px-2 py-1">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Fair Management
                  </p>
                </div>
                <Link
                  to="/fairmanager/slots"
                  className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                  data-testid="nav-slots"
                >
                  <Grid3x3 className="h-4 w-4" />
                  <span>Slot Management</span>
                </Link>
                {pricingTemplateEnabled && (
                  <Link
                    to="/fairmanager/pricing"
                    className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                  >
                    <Tag className="h-4 w-4" />
                    <span>Pricing Templates</span>
                  </Link>
                )}
                {rentalEquipmentEnabled && (
                  <Link
                    to="/fairmanager/rentals"
                    className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                  >
                    <Package className="h-4 w-4" />
                    <span>Rental Equipment</span>
                  </Link>
                )}
                <Link
                  to="/fairmanager/reservations"
                  className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                  data-testid="nav-reservations"
                >
                  <Calendar className="h-4 w-4" />
                  <span>Reservations</span>
                </Link>
                <Link
                  to="/fairmanager/inquiries"
                  className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                  data-testid="nav-inquiries"
                >
                  <MessageSquare className="h-4 w-4" />
                  <span>Inquiries</span>
                </Link>
                <Link
                  to="/fairmanager/waitlist"
                  className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                  data-testid="nav-waitlist"
                >
                  <Users className="h-4 w-4" />
                  <span>Waitlist</span>
                </Link>
                <Link
                  to="/fairmanager/statistics"
                  className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                >
                  <BarChart3 className="h-4 w-4" />
                  <span>Statistics</span>
                </Link>
              </nav>
            </div>
          </div>

          {/* Mobile Sidebar Overlay (shown when hamburger is clicked) */}
          {sidebarOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden"
                onClick={() => setSidebarOpen(false)}
              />

              {/* Sidebar Panel */}
              <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl z-50 lg:hidden overflow-y-auto">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Fair Manager Menu</h2>
                    <button
                      type="button"
                      onClick={() => setSidebarOpen(false)}
                      className="p-2 rounded-md hover:bg-gray-100"
                      aria-label="Close menu"
                      title="Close menu"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <nav className="space-y-2" data-testid="fairmanager-nav">
                    <Link
                      to="/fairmanager/dashboard"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <Home className="h-4 w-4" />
                      <span>Dashboard</span>
                    </Link>
                    <Link
                      to="/fairmanager/fairs"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                      data-testid="nav-my-fairs"
                    >
                      <List className="h-4 w-4" />
                      <span>My Fairs</span>
                    </Link>
                    <Link
                      to="/fairmanager/fair/new"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Create New Fair</span>
                    </Link>
                    <Link
                      to="/fairmanager/payouts"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                      data-testid="nav-payouts"
                    >
                      <DollarSign className="h-4 w-4" />
                      <span>Payouts</span>
                    </Link>

                    {/* Divider */}
                    <div className="border-t border-gray-200 my-2"></div>

                    {/* Note: Routes for the following items need to be added in follow-up work
                        (Issues #371, #372). These navigation links are intentionally included
                        to show the complete Fair Manager feature set, but will render blank
                        pages until the corresponding routes and components are implemented. */}
                    <div className="px-2 py-1">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Fair Management
                      </p>
                    </div>
                    <Link
                      to="/fairmanager/slots"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                      data-testid="nav-slots"
                    >
                      <Grid3x3 className="h-4 w-4" />
                      <span>Slot Management</span>
                    </Link>
                    {pricingTemplateEnabled && (
                      <Link
                        to="/fairmanager/pricing"
                        onClick={() => setSidebarOpen(false)}
                        className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                      >
                        <Tag className="h-4 w-4" />
                        <span>Pricing Templates</span>
                      </Link>
                    )}
                    {rentalEquipmentEnabled && (
                      <Link
                        to="/fairmanager/rentals"
                        onClick={() => setSidebarOpen(false)}
                        className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                      >
                        <Package className="h-4 w-4" />
                        <span>Rental Equipment</span>
                      </Link>
                    )}
                    <Link
                      to="/fairmanager/reservations"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                      data-testid="nav-reservations"
                    >
                      <Calendar className="h-4 w-4" />
                      <span>Reservations</span>
                    </Link>
                    <Link
                      to="/fairmanager/inquiries"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                      data-testid="nav-inquiries"
                    >
                      <MessageSquare className="h-4 w-4" />
                      <span>Inquiries</span>
                    </Link>
                    <Link
                      to="/fairmanager/waitlist"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                      data-testid="nav-waitlist"
                    >
                      <Users className="h-4 w-4" />
                      <span>Waitlist</span>
                    </Link>
                    <Link
                      to="/fairmanager/statistics"
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <BarChart3 className="h-4 w-4" />
                      <span>Statistics</span>
                    </Link>
                  </nav>
                </div>
              </div>
            </>
          )}

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
